import { addDays, endOfDay, startOfDay } from "date-fns";

import type { ListScheduledQuestionnairesControllerListScheduledQuestionnairesParams } from "@/generated/model/listScheduledQuestionnairesControllerListScheduledQuestionnairesParams";
import type { ListScheduledQuestionnairesControllerListScheduledQuestionnairesSortDir } from "@/generated/model/listScheduledQuestionnairesControllerListScheduledQuestionnairesSortDir";
import type { ScheduledQuestionnaireListItemDto } from "@/generated/model/scheduledQuestionnaireListItemDto";
import {
  useChecklistSummaryControllerGetSummary,
  useListScheduledQuestionnairesControllerListScheduledQuestionnaires,
} from "@/generated/scheduled-questionnaires/scheduled-questionnaires";
import {
  type ChecklistManagerTab,
  type ChecklistSortColumn,
  DUE_SOON_HORIZON_DAYS,
  DUE_SOON_STATUSES,
  HISTORY_STATUSES,
  type SortDir,
} from "@/lib/checklist-manager";

interface UseChecklistManagerDataArgs {
  nurseryId?: string;
  tab: ChecklistManagerTab;
  categoryId?: string;
  search?: string;
  sortBy?: ChecklistSortColumn;
  sortDir: SortDir;
  page: number;
  pageSize: number;
}

/**
 * Drives one Checklist Manager list tab server-side: maps the active tab to the
 * status / failed-checks / window filters, and forwards category, search, sort
 * and pagination to the API. Badge counts come from the dedicated summary
 * endpoint so they stay consistent with the tab contents regardless of paging.
 */
export function useChecklistManagerData({
  nurseryId,
  tab,
  categoryId,
  search,
  sortBy,
  sortDir,
  page,
  pageSize,
}: UseChecklistManagerDataArgs) {
  const params: ListScheduledQuestionnairesControllerListScheduledQuestionnairesParams =
    {
      limit: pageSize,
      offset: page * pageSize,
      includeCompletedBy: true,
      categoryId: categoryId || undefined,
      search: search || undefined,
      sortBy,
      sortDir:
        sortDir as ListScheduledQuestionnairesControllerListScheduledQuestionnairesSortDir,
    };

  if (tab === "history") {
    params.status = HISTORY_STATUSES;
  } else if (tab === "due-soon") {
    // Due soon is bounded to a forward horizon regardless of any period.
    params.status = DUE_SOON_STATUSES;
    params.startDate = startOfDay(new Date()).toISOString();
    params.endDate = endOfDay(
      addDays(new Date(), DUE_SOON_HORIZON_DAYS),
    ).toISOString();
  } else if (tab === "failed") {
    params.failedChecksOnly = true;
  }

  const listEnabled = !!nurseryId && tab !== "templates";

  const listQuery =
    useListScheduledQuestionnairesControllerListScheduledQuestionnaires(
      nurseryId ?? "",
      params,
      { query: { enabled: listEnabled } },
    );

  const summaryQuery = useChecklistSummaryControllerGetSummary(
    nurseryId ?? "",
    undefined,
    { query: { enabled: !!nurseryId } },
  );

  const items: ScheduledQuestionnaireListItemDto[] =
    listQuery.data?.data.items ?? [];
  const total: number = listQuery.data?.data.total ?? 0;

  return {
    items,
    total,
    isLoading: listQuery.isLoading,
    dueSoonCount: summaryQuery.data?.data.dueSoonCount ?? 0,
    failedChecksCount: summaryQuery.data?.data.failedChecksCount ?? 0,
  };
}
